import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { GoogleMap, LoadScript, Polyline } from '@react-google-maps/api';
import { Map } from 'immutable';
import env from 'lib/env';
import Search from 'models/search';
import { Location } from 'models/locations';
import LoadingPulse from 'components/common/loading-pulse';

const { GOOGLE_MAPS_API_KEY } = env();
const DEFAULT_INITIAL_ZOOM = 16;
const IDEAL_NUM_LOCATIONS = 5;
const STREET_PARKING_ZOOM_THRESHOLD = 16;
const POLYLINE_STROKE_WEIGHT_HIGH = 4;
const POLYLINE_STROKE_WEIGHT_LOW = 1;
const POLYLINE_STROKE_WEIGHT_DEFAULT = 2;
const POLYLINE_STROKE_OPACITY = 1.0;

export const CONTROL_POSITIONS = {
  BOTTOM_CENTER: 11,
  BOTTOM_LEFT: 10,
  BOTTOM_RIGHT: 12,
  LEFT_BOTTOM: 6,
  LEFT_CENTER: 4,
  LEFT_TOP: 5,
  RIGHT_BOTTOM: 9,
  RIGHT_CENTER: 8,
  RIGHT_TOP: 7,
  TOP_CENTER: 2,
  TOP_LEFT: 1,
  TOP_RIGHT: 3,
};

export class GoogleMapView extends Component {
  constructor(props) {
    super(props);
    this.map = null;
    this.state = {
      zoom: props.currentSearch.zoomLevel || DEFAULT_INITIAL_ZOOM,
    };
    this.triggerBoundsChange = this.triggerBoundsChange.bind(this);
    this.fitBoundsToResults = this.fitBoundsToResults.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (!this.map) return;

    const { currentSearch, locations, selectedLocation } = this.props;
    const prevSearch = prevProps.currentSearch;

    const destinationChanged = currentSearch.destination !== prevSearch.destination;
    const parkingTypeChanged = currentSearch.parkingType !== prevSearch.parkingType;
    const selectionChanged = selectedLocation !== prevProps.selectedLocation;
    const initialLoad = (!prevProps.locations || prevProps.locations.size === 0) && (locations && locations.size > 0);

    if (destinationChanged || parkingTypeChanged || selectionChanged || initialLoad) {
      const isDeselection = !!(selectionChanged && !selectedLocation && prevProps.selectedLocation);
      this.fitBoundsToResults(isDeselection);
    }
  }

  shouldAddQuoteToBounds(quote, { numLocations, numEventPricings }) {
    if (!quote) { return false; }
    const { limitEventZoomToFirstTen, currentSearch } = this.props;

    // The distance check is commented out because it was actually not working after a [refactor](https://github.com/ParkWhiz/parkwhiz.com/commit/20fd33956c301ceeef70bfbf7dcc399875438c2c)
    // as the `location` variable in this context was `window.location` that doesn't have distance and was then always false
    // this cause some locations like [Chicago O'Hare Internation](https://local.parkwhiz.com:3000/o-hare-airport-parking/) 
    // to have a bigger zoom, and shows locations out the IDEAL_LOCATION_DISTANCE
  
    // We only want to show locations that fall into the ideal zoom
    const idealZoomReached = numLocations >= IDEAL_NUM_LOCATIONS; // || location.distance >= IDEAL_LOCATION_DISTANCE;
    // Exception is 1) Locations with shuttles
    // 2) Event Pricings
    // 3) Event Packages
    const { isEventRelated } = quote;

    if (limitEventZoomToFirstTen && currentSearch.isEventSearch) {
      return !idealZoomReached || (numEventPricings < 10 && isEventRelated);
    }

    return !idealZoomReached || isEventRelated;
  }

  fitBoundsToResults(isDeselection = false) {
    if (typeof window === 'undefined' || !this.map || !window.google) return;

    const { currentSearch, locations, selectedLocation } = this.props;
    const bounds = new window.google.maps.LatLngBounds();

    if (isDeselection && currentSearch.bounds && currentSearch.bounds.size === 4) {
      const neParts = currentSearch.bounds.get(1).split(',');
      const swParts = currentSearch.bounds.get(3).split(',');
      const neLat = parseFloat(neParts[0]);
      const neLng = parseFloat(neParts[1]);
      const swLat = parseFloat(swParts[0]);
      const swLng = parseFloat(swParts[1]);

      if (!isNaN(neLat) && !isNaN(neLng) && !isNaN(swLat) && !isNaN(swLng)) {
        bounds.extend({ lat: neLat, lng: neLng });
        bounds.extend({ lat: swLat, lng: swLng });
      }
    } else {
      const currentLat = currentSearch.anchorLat;
      const currentLng = currentSearch.anchorLng;

      // 1. Include search destination / anchor
      bounds.extend({ lat: currentLat, lng: currentLng });

      if (selectedLocation) {
        // 2. Fit to selected location entrance + search destination
        const entrance = selectedLocation.entrances.first();
        if (entrance) {
          bounds.extend({ lat: entrance.lat, lng: entrance.lng });
        }
      } else if (locations && locations.size > 0) {
        // 3. Include nearby search results
        let numLocations = 0;
        let numEventPricings = 0;

        locations.sortBy(l => l.distance).forEach((location) => {
          const quote = location.getQuote();
          if (this.shouldAddQuoteToBounds(quote, { numLocations, numEventPricings })) {
            const entrance = location.entrances.get(0);
            if (entrance) {
              bounds.extend({ lat: entrance.lat, lng: entrance.lng });
              numLocations += 1;
              if (quote.isEventRelated) {
                numEventPricings += 1;
              }
            }
          }
        });
      }
    }

    // Call fitBounds with Mapbox-aligned asymmetrical padding
    const padding = isDeselection
      ? { top: 0, bottom: 0, left: 0, right: 0 }
      : (selectedLocation
        ? { top: 100, bottom: 25, left: 50, right: 100 }
        : { top: 75, bottom: 25, left: 25, right: 100 });

    const maxZoom = selectedLocation ? 19 : 17;
    const currentZoom = this.map.getZoom();
    if (!selectedLocation && currentZoom > 17) {
      this.map.setOptions({ maxZoom: currentZoom });
    } else {
      this.map.setOptions({ maxZoom });
    }

    this.map.fitBounds(bounds, padding);

    // Reset maxZoom once the camera transition is complete so the user can zoom manually
    window.google.maps.event.addListenerOnce(this.map, 'idle', () => {
      if (this.map) {
        this.map.setOptions({ maxZoom: null });
      }
    });
  }

  triggerBoundsChange() {
    if (!this.map) return;

    const zoomLevel = this.map.getZoom();
    if (this.state.zoom !== zoomLevel) {
      this.setState({ zoom: zoomLevel });
    }

    if (this.props.selectedLocation) return;

    const { currentSearch, boundsChange } = this.props;
    const bounds = this.map.getBounds();
    const center = this.map.getCenter();

    if (!bounds || !center) return;

    const lat = center.lat();
    const lng = center.lng();

    // Avoid infinite loops by skipping updates if coordinates are unchanged
    const searchLat = currentSearch.lat || currentSearch.anchorLat;
    const searchLng = currentSearch.lng || currentSearch.anchorLng;
    const searchZoom = currentSearch.zoomLevel || DEFAULT_INITIAL_ZOOM;

    const latDiff = Math.abs(lat - searchLat);
    const lngDiff = Math.abs(lng - searchLng);
    const zoomDiff = Math.abs(zoomLevel - searchZoom);

    if (latDiff < 0.0001 && lngDiff < 0.0001 && zoomDiff < 0.1) {
      return;
    }

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    const mockBounds = {
      getNorthEast: () => ({ lat: ne.lat(), lng: ne.lng() }),
      getSouthWest: () => ({ lat: sw.lat(), lng: sw.lng() }),
    };

    boundsChange({
      bounds: mockBounds,
      lat,
      lng,
      zoomLevel,
      router: false,
    });
  }

  renderStreetParkingPolylines() {
    const { currentSearch, streetParking, showStreetParking, selectedLocation } = this.props;
    if (
      currentSearch.isMonthlySearch ||
      !showStreetParking ||
      selectedLocation ||
      !streetParking ||
      streetParking.size === 0
    ) {
      return null;
    }

    const { zoom } = this.state;
    const strokeWeight = zoom > STREET_PARKING_ZOOM_THRESHOLD
      ? POLYLINE_STROKE_WEIGHT_HIGH
      : (zoom < STREET_PARKING_ZOOM_THRESHOLD ? POLYLINE_STROKE_WEIGHT_LOW : POLYLINE_STROKE_WEIGHT_DEFAULT);

    return streetParking.map((segment) => {
      const path = segment.geometries.map(g => ({ lat: g.lat, lng: g.lng })).toArray();
      return (
        <Polyline
          key={segment.id}
          path={path}
          options={{
            strokeColor: segment.color,
            strokeWeight,
            strokeOpacity: POLYLINE_STROKE_OPACITY,
          }}
        />
      );
    }).toArray();
  }

  render() {
    const { currentSearch, zoomControlPosition } = this.props;
    const lat = currentSearch.lat || currentSearch.anchorLat;
    const lng = currentSearch.lng || currentSearch.anchorLng;
    const zoom = currentSearch.zoomLevel || DEFAULT_INITIAL_ZOOM;

    if (lat === null || lat === undefined || lng === null || lng === undefined || isNaN(lat) || isNaN(lng)) {
      return (
        <div style={{ height: '100%', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <LoadingPulse visible={true} />
        </div>
      );
    }

    return (
      <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY}>
        <GoogleMap
          mapContainerStyle={{ height: '100%', width: '100%' }}
          defaultCenter={{ lat, lng }}
          defaultZoom={zoom}
          onClick={this.props.onMapClick}
          onLoad={(map) => { 
            this.map = map; 
            this.fitBoundsToResults();
          }}
          onDragEnd={() => this.triggerBoundsChange()}
          onZoomChanged={() => this.triggerBoundsChange()}
          clickableIcons={false}
          options={{
            disableDefaultUI: true,
            gestureHandling: 'greedy',
            zoomControl: true,
            zoomControlOptions: {
              position: zoomControlPosition,
            },
          }}
        >
          {this.props.children}
          {this.renderStreetParkingPolylines()}
        </GoogleMap>
      </LoadScript>
    );
  }
}

GoogleMapView.propTypes = {
  currentSearch: PropTypes.instanceOf(Search).isRequired,
  onMapClick: PropTypes.func,
  selectedLocation: PropTypes.instanceOf(Location),
  locations: PropTypes.any,
  boundsChange: PropTypes.func.isRequired,
  children: PropTypes.node,
  streetParking: PropTypes.instanceOf(Map),
  showStreetParking: PropTypes.bool,
  zoomControlPosition: PropTypes.oneOf(Object.values(CONTROL_POSITIONS)),
  limitEventZoomToFirstTen: PropTypes.bool,
};

GoogleMapView.defaultProps = {
  onMapClick: () => {},
  selectedLocation: null,
  locations: null,
  children: null,
  streetParking: null,
  showStreetParking: false,
  zoomControlPosition: CONTROL_POSITIONS.TOP_RIGHT,
  limitEventZoomToFirstTen: false,
};

export default GoogleMapView;
